import { useEffect, useRef } from 'react'
import Moengage from '@moengage/web-sdk'

import AndroidHandler from 'utils/androidHandler'
import { useTogglesStore } from 'stores/toggles'

const isBrowser = typeof window !== 'undefined'

type UseMoengageParams = {
  id?: string
  email?: string
  fullName?: string
  firstName?: string
  lastName?: string
  namaSekolah?: string
  npsn?: string
  provinsi?: string
} | null

const useMoengage = (
  user: UseMoengageParams,
  tokenExpiredAt?: string,
  disabled?: boolean
) => {
  const isMoengageIntegrationEnabled = useTogglesStore(
    ({ toggles }) => toggles?.['moengage-integration-enabled'] || false
  )
  const prevUserRef = useRef<UseMoengageParams>(null)

  // Initialize MoEngage once
  useEffect(() => {
    const shouldInit = isMoengageIntegrationEnabled
    const isInAndroid = AndroidHandler()?.enabled

    if (!shouldInit || !isBrowser || isInAndroid || disabled) return

    const MOE_APP_ID = process.env.NEXT_PUBLIC_MOE_APP_ID ?? ''
    const MOE_CLUSTER = process.env.NEXT_PUBLIC_MOE_REGION ?? ''

    if (!(window as any)._moe_initialized) {
      Moengage.initialize({
        app_id: MOE_APP_ID,
        cluster: MOE_CLUSTER,
        debug_logs: process.env.NEXT_PUBLIC_MOE_DEBUG === 'true' ? 1 : 0,
        useLatest: true,
        swPath: 'serviceworker.js',
        enableSPA: false,
      })
      ;(window as any)._moe_initialized = true
    }
  }, [isMoengageIntegrationEnabled, disabled])

  // Timer-based session destroy and user check
  useEffect(() => {
    if (!tokenExpiredAt) return

    const expiry = new Date(tokenExpiredAt).getTime()
    const now = Date.now()
    const timeout = expiry - now

    // If already expired, destroy session immediately
    if (timeout <= 0) {
      Moengage.destroy_session?.()
      prevUserRef.current = null
      return
    }

    // Otherwise, set up a timer to destroy session at expiry
    const timer = setTimeout(() => {
      Moengage.destroy_session?.()
      prevUserRef.current = null
    }, timeout)

    // Clean up timer on unmount or user change
    return () => clearTimeout(timer)
  }, [tokenExpiredAt])

  // Set user attributes only if not expired and data changed
  useEffect(() => {
    const isUserExpired =
      tokenExpiredAt && new Date(tokenExpiredAt).getTime() < Date.now()

    if (isUserExpired) {
      // Session destruction handled above; skip setting user attributes
      return
    }

    const isSameUser =
      JSON.stringify(user) === JSON.stringify(prevUserRef.current)

    if (!isSameUser && user) {
      const { id, email, firstName, lastName, fullName, ...attrs } = user

      if (id) Moengage.add_unique_user_id(id)
      if (email) Moengage.add_email(email)
      if (firstName) Moengage.add_first_name(firstName)
      if (lastName) Moengage.add_first_name(lastName)
      if (fullName) Moengage.add_user_name(fullName)

      Object.entries(attrs).forEach(([key, value]) => {
        if (typeof value !== undefined && value !== null) {
          Moengage.add_user_attribute(key, value)
        }
      })

      prevUserRef.current = user
    }
  }, [tokenExpiredAt, user])
}

export default useMoengage
